import { CSSProperties } from 'react';

// Configuration object for all content and styling
interface PricingConfig {
  styles: {
    colors: {
      backgroundGradientFrom: string;
      backgroundGradientVia: string;
      backgroundGradientTo: string;
      cardBackground: string;
      cardBorderGradientFrom: string;
      cardBorderGradientTo: string;
      textPrimary: string;
      textSecondary: string;
      textMuted: string;
      buttonGradientFrom: string;
      buttonGradientTo: string;
      buttonText: string;
      buttonHoverGradientFrom: string;
      buttonHoverGradientTo: string;
    };
    typography: {
      titleSize: string;
      titleWeight: string;
      subtitleSize: string;
      subtitleWeight: string;
      planNameSize: string;
      planNameWeight: string;
      priceSize: string;
      priceWeight: string;
      featureLabelSize: string;
      featureLabelWeight: string;
      featureValueSize: string;
      featureValueWeight: string;
      buttonSize: string;
      buttonWeight: string;
    };
    spacing: {
      sectionPaddingY: string;
      sectionPaddingX: string;
      cardPadding: string;
      cardGap: string;
      cardBorderRadius: string;
      cardBorderWidth: string;
      buttonPaddingY: string;
      buttonPaddingX: string;
      buttonBorderRadius: string;
    };
  };
  content: {
    title: string;
    subtitle: string;
    plans: Array<{
      name: string;
      setupFee: {
        label: string;
        amount: string;
        period: string;
      };
      subscription: {
        label: string;
        amount: string;
        note: string;
      };
      button: string;
    }>;
  };
}

const defaultConfig: PricingConfig = {
  styles: {
    colors: {
      backgroundGradientFrom: 'transparent',
      backgroundGradientVia: 'transparent',
      backgroundGradientTo: 'transparent',
      cardBackground: 'rgba(0, 0, 0, 0.3)',
      cardBorderGradientFrom: 'rgb(var(--color1))',
      cardBorderGradientTo: 'rgb(var(--color2))',
      textPrimary: '#ffffff',
      textSecondary: '#F5F3FF',
      textMuted: 'rgba(255, 255, 255, 0.7)',
      buttonGradientFrom: 'rgb(var(--color1))',
      buttonGradientTo: 'rgb(var(--color2))',
      buttonText: '#ffffff',
      buttonHoverGradientFrom: 'rgba(var(--color1), 0.8)',
      buttonHoverGradientTo: 'rgba(var(--color2), 0.8)',
    },
    typography: {
      titleSize: '80px',
      titleWeight: '800',
      subtitleSize: '14px',
      subtitleWeight: '400',
      planNameSize: '22px',
      planNameWeight: '600',
      priceSize: '64px',
      priceWeight: '800',
      featureLabelSize: '13px',
      featureLabelWeight: '400',
      featureValueSize: '16px',
      featureValueWeight: '600',
      buttonSize: '13px',
      buttonWeight: '700',
    },
    spacing: {
      sectionPaddingY: '60px',
      sectionPaddingX: '24px',
      cardPadding: '44px',
      cardGap: '24px',
      cardBorderRadius: '18px',
      cardBorderWidth: '2px',
      buttonPaddingY: '15px',
      buttonPaddingX: '48px',
      buttonBorderRadius: '100px',
    },
  },
  content: {
    title: 'Pricing',
    subtitle: 'You pick the approach based on your confidence. We help you grow.',
    plans: [
      {
        name: 'The Cautious Creator',
        setupFee: {
          label: 'One-time setup fee:',
          amount: "$4'999",
          period: '',
        },
        subscription: {
          label: 'Monthly subscription:',
          amount: '$799',
          note: 'Revenue-sharing: 0% of the GRR',
        },
        button: 'CHOOSE A PLAN',
      },
      {
        name: 'The All-In Pro',
        setupFee: {
          label: 'One-time setup fee:',
          amount: "$19'999",
          period: '',
        },
        subscription: {
          label: 'Monthly subscription:',
          amount: 'None',
          note: 'Revenue-sharing: 10% of the GRR',
        },
        button: 'CHOOSE A PLAN',
      },
    ],
  },
};

interface PricingSectionProps {
  config?: Partial<PricingConfig>;
  onClick?: () => void;
}

export default function PricingSection({ config, onClick }: PricingSectionProps) {
  const mergedConfig: PricingConfig = {
    styles: {
      colors: { ...defaultConfig.styles.colors, ...config?.styles?.colors },
      typography: { ...defaultConfig.styles.typography, ...config?.styles?.typography },
      spacing: { ...defaultConfig.styles.spacing, ...config?.styles?.spacing },
    },
    content: {
      ...defaultConfig.content,
      ...config?.content,
      plans: config?.content?.plans || defaultConfig.content.plans,
    },
  };

  const { styles, content } = mergedConfig;

  const sectionStyle: CSSProperties = {
    minHeight: '100vh',
    background: 'transparent',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: `${styles.spacing.sectionPaddingY} ${styles.spacing.sectionPaddingX}`,
    fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    position: 'relative',
    overflow: 'hidden',
  };

  const titleWrapperStyle: CSSProperties = {
    position: 'relative',
    display: 'inline-block',
    marginBottom: '38px',
  };

  const titleStyle: CSSProperties = {
    fontSize: styles.typography.titleSize,
    fontWeight: styles.typography.titleWeight,
    color: '#ffffff',
    margin: '0',
    textAlign: 'center',
    letterSpacing: '-0.02em',
    position: 'relative',
    zIndex: 2,
    textShadow: `
      0 0 20px rgba(var(--color1), 1),
      0 0 40px rgba(var(--color1), 0.8),
      0 0 60px rgba(var(--color2), 0.6),
      0 0 80px rgba(var(--color2), 0.4),
      0 0 100px rgba(var(--color1), 0.3)
    `,
  };

  const titleGlowLineStyle: CSSProperties = {
    position: 'absolute',
    bottom: '-15px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '75%',
    height: '6px',
    background: `radial-gradient(ellipse at center, rgba(var(--color1), 0.85) 0%, rgba(var(--color2), 0.4) 50%, transparent 70%)`,
    borderRadius: '50%',
    filter: 'blur(10px)',
    opacity: 0.85,
    zIndex: 1,
  };

  const titleBackgroundGlowStyle: CSSProperties = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '140%',
    height: '140%',
    background: `radial-gradient(ellipse at center, rgba(var(--color1), 0.25) 0%, rgba(var(--color2), 0.12) 40%, transparent 70%)`,
    filter: 'blur(50px)',
    zIndex: 0,
  };

  const subtitleStyle: CSSProperties = {
    fontSize: styles.typography.subtitleSize,
    fontWeight: styles.typography.subtitleWeight,
    color: 'rgba(255, 255, 255, 0.8)',
    margin: '0 0 53px 0',
    textAlign: 'center',
    maxWidth: '600px',
    lineHeight: '1.6',
    letterSpacing: '0.02em',
  };

  const cardsContainerStyle: CSSProperties = {
    display: 'flex',
    gap: styles.spacing.cardGap,
    flexWrap: 'wrap',
    justifyContent: 'center',
    maxWidth: '1000px',
    width: '100%',
    filter: `drop-shadow(0 0 40px rgba(var(--color1), 0.25))`,
  };

  const cardStyle: CSSProperties = {
    flex: '1',
    minWidth: '340px',
    maxWidth: '480px',
    background: styles.colors.cardBackground,
    backdropFilter: 'blur(30px)',
    WebkitBackdropFilter: 'blur(30px)',
    borderRadius: styles.spacing.cardBorderRadius,
    padding: styles.spacing.cardPadding,
    position: 'relative',
    overflow: 'hidden',
    boxShadow: `0 0 25px rgba(var(--color1), 0.5), 0 0 45px rgba(var(--color2), 0.3), 0 20px 60px rgba(0, 0, 0, 0.7)`,
  };

  const cardBorderStyle: CSSProperties = {
    content: '""',
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    borderRadius: styles.spacing.cardBorderRadius,
    padding: styles.spacing.cardBorderWidth,
    background: `linear-gradient(135deg, ${styles.colors.cardBorderGradientFrom}, ${styles.colors.cardBorderGradientTo})`,
    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
    pointerEvents: 'none',
  };

  const cardInnerGlowStyle: CSSProperties = {
    content: '""',
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    height: '50%',
    background: `linear-gradient(180deg, rgba(var(--color1), 0.1) 0%, transparent 100%)`,
    borderRadius: `${styles.spacing.cardBorderRadius} ${styles.spacing.cardBorderRadius} 0 0`,
    pointerEvents: 'none',
  };

  const planNameStyle: CSSProperties = {
    fontSize: styles.typography.planNameSize,
    fontWeight: styles.typography.planNameWeight,
    color: styles.colors.textPrimary,
    margin: '0 0 36px 0',
    letterSpacing: '0.01em',
  };

  const priceBlockStyle: CSSProperties = {
    marginBottom: '19px',
  };

  const featureLabelStyle: CSSProperties = {
    fontSize: styles.typography.featureLabelSize,
    fontWeight: styles.typography.featureLabelWeight,
    color: 'rgba(255, 255, 255, 0.7)',
    margin: '0 0 12px 0',
    letterSpacing: '0.01em',
  };

  const priceStyle: CSSProperties = {
    fontSize: styles.typography.priceSize,
    fontWeight: styles.typography.priceWeight,
    color: styles.colors.textPrimary,
    margin: '0',
    lineHeight: '1',
    letterSpacing: '-0.04em',
  };

  const subscriptionBlockStyle: CSSProperties = {
    marginTop: '19px',
  };

  const subscriptionLabelStyle: CSSProperties = {
    fontSize: styles.typography.featureLabelSize,
    fontWeight: styles.typography.featureLabelWeight,
    color: 'rgba(255, 255, 255, 0.7)',
    margin: '0 0 8px 0',
    letterSpacing: '0.01em',
  };

  const subscriptionValueStyle: CSSProperties = {
    fontSize: styles.typography.featureValueSize,
    fontWeight: styles.typography.featureValueWeight,
    color: styles.colors.textSecondary,
    margin: '0 0 10px 0',
    letterSpacing: '0.01em',
  };

  const subscriptionNoteStyle: CSSProperties = {
    fontSize: styles.typography.featureLabelSize,
    fontWeight: styles.typography.featureLabelWeight,
    color: 'rgba(255, 255, 255, 0.7)',
    margin: '0',
    letterSpacing: '0.01em',
  };

  const buttonStyle: CSSProperties = {
    width: '100%',
    padding: `${styles.spacing.buttonPaddingY} ${styles.spacing.buttonPaddingX}`,
    fontSize: styles.typography.buttonSize,
    fontWeight: styles.typography.buttonWeight,
    color: styles.colors.buttonText,
    background: `linear-gradient(90deg, ${styles.colors.buttonGradientFrom} 0%, ${styles.colors.buttonGradientTo} 100%)`,
    border: 'none',
    borderRadius: styles.spacing.buttonBorderRadius,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    letterSpacing: '0.1em',
    marginTop: '52px',
    boxShadow: `0 0 40px rgba(var(--color1), 0.7), 0 0 80px rgba(var(--color2), 0.5), 0 6px 24px rgba(0, 0, 0, 0.4)`,
    position: 'relative',
    overflow: 'hidden',
  };

  const buttonInnerGlowStyle: CSSProperties = {
    content: '""',
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    height: '50%',
    background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.25) 0%, transparent 100%)',
    borderRadius: `${styles.spacing.buttonBorderRadius} ${styles.spacing.buttonBorderRadius} 0 0`,
    pointerEvents: 'none',
  };

  return (
    <div id="pricing" style={sectionStyle}>
      <div style={titleWrapperStyle}>
        <div style={titleBackgroundGlowStyle}></div>
        <h1 style={titleStyle}>{content.title}</h1>
        <div style={titleGlowLineStyle}></div>
      </div>
      <p style={subtitleStyle}>{content.subtitle}</p>
      
      <div style={cardsContainerStyle}>
        {content.plans.map((plan, index) => (
          <div key={index} style={cardStyle}>
            <div style={cardBorderStyle}></div>
            <div style={cardInnerGlowStyle}></div>
            <div style={{ position: 'relative', zIndex: 1 }}>
              <h2 style={planNameStyle}>{plan.name}</h2>
              
              <div style={priceBlockStyle}>
                <p style={featureLabelStyle}>{plan.setupFee.label}</p>
                <h3 style={priceStyle}>{plan.setupFee.amount}</h3>
              </div>
              
              <div style={subscriptionBlockStyle}>
                <p style={subscriptionLabelStyle}>{plan.subscription.label}</p>
                <p style={subscriptionValueStyle}>{plan.subscription.amount}</p>
                <p style={subscriptionNoteStyle}>{plan.subscription.note}</p>
              </div>
              
              <button 
                style={buttonStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = `linear-gradient(90deg, ${styles.colors.buttonHoverGradientFrom} 0%, ${styles.colors.buttonHoverGradientTo} 100%)`;
                  e.currentTarget.style.transform = 'translateY(-3px) scale(1.01)';
                  e.currentTarget.style.boxShadow = `0 0 60px rgba(var(--color1), 0.9), 0 0 120px rgba(var(--color2), 0.7), 0 10px 40px rgba(0, 0, 0, 0.5)`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = `linear-gradient(90deg, ${styles.colors.buttonGradientFrom} 0%, ${styles.colors.buttonGradientTo} 100%)`;
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = `0 0 40px rgba(var(--color1), 0.7), 0 0 80px rgba(var(--color2), 0.5), 0 6px 24px rgba(0, 0, 0, 0.4)`;
                }}
                // onClick={() => console.log(`${plan.name} - ${plan.button} clicked`)}
                onClick={onClick}
                data-testid={`button-choose-plan-${index}`}
              >
                <div style={buttonInnerGlowStyle}></div>
                <span style={{ position: 'relative', zIndex: 1 }}>{plan.button}</span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
